import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import PendingIcon from '@mui/icons-material/Pending';
import { useAuth } from '../../context/AuthContext';
import { UtilityService } from '../../types/utility.types';
import api from '../../utils/api';

interface SubBrokerStats {
  totalContracts: number;
  totalCommissionValue: number;
  totalCommissionReceived: number;
  outstandingCommission: number;
  liveContracts: number;
  pendingContracts: number;
}

const SubBrokerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState<UtilityService[]>([]);
  const [stats, setStats] = useState<SubBrokerStats>({
    totalContracts: 0,
    totalCommissionValue: 0,
    totalCommissionReceived: 0,
    outstandingCommission: 0,
    liveContracts: 0,
    pendingContracts: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchSubBrokerData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/utility-services');
      const data = response.data;

      // Filter contracts where current user is the sub-broker
      const myContracts = data.filter((contract: UtilityService) => 
        contract.subBroker && contract.subBroker.userId === user?._id
      );

      setContracts(myContracts);

      // Calculate stats
      const calculatedStats: SubBrokerStats = {
        totalContracts: myContracts.length,
        totalCommissionValue: 0,
        totalCommissionReceived: 0,
        outstandingCommission: 0,
        liveContracts: 0,
        pendingContracts: 0
      };

      myContracts.forEach((contract: UtilityService) => {
        const contractValue = Number(contract.contractValue) || 0;
        const commissionRate = contract.subBroker?.commissionRate || 0;
        const myCommission = contractValue * (commissionRate / 100);
        
        calculatedStats.totalCommissionValue += myCommission;

        // Calculate received commission (proportional to what's been paid)
        const totalReceived = Number(contract.totalCommissionReceived) || 0;
        const totalExpected = contractValue;
        const receivedPercentage = totalExpected > 0 ? totalReceived / totalExpected : 0;
        const myReceivedCommission = myCommission * receivedPercentage;
        
        calculatedStats.totalCommissionReceived += myReceivedCommission;
        calculatedStats.outstandingCommission += (myCommission - myReceivedCommission);

        // Count by status
        if (contract.status === 'Live' || contract.status === 'LIVE') {
          calculatedStats.liveContracts++;
        } else if (contract.status === 'Pending' || contract.status === 'PENDING') {
          calculatedStats.pendingContracts++;
        }
      });

      setStats(calculatedStats);
    } catch (error) {
      console.error('Error fetching sub-broker data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubBrokerData();
  }, [user]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'LIVE':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'TERMINATED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {user?.subBrokerCompanyName || 'Sub-Broker Dashboard'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back, {user?.firstName} {user?.lastName}
            {user?.subBrokerLevel && (
              <>
                {' • '}
                <Chip 
                  label={user.subBrokerLevel}
                  size="small"
                  color="secondary"
                  sx={{ ml: 0.5, height: '20px' }}
                />
              </>
            )}
          </Typography>
        </Box>
        <Tooltip title="Refresh data">
          <IconButton onClick={fetchSubBrokerData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            borderRadius: 2
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttachMoneyIcon sx={{ color: '#4caf50', mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Commission Value
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#4caf50' }}>
                {formatCurrency(stats.totalCommissionValue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                From {stats.totalContracts} contracts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(33, 150, 243, 0.3)',
            borderRadius: 2
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#2196f3', mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Commission Received
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#2196f3' }}>
                {formatCurrency(stats.totalCommissionReceived)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.totalCommissionValue > 0 
                  ? `${((stats.totalCommissionReceived / stats.totalCommissionValue) * 100).toFixed(1)}% collected`
                  : '0% collected'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 152, 0, 0.3)',
            borderRadius: 2
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PendingIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Outstanding Commission
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#ff9800' }}>
                {formatCurrency(stats.outstandingCommission)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.totalCommissionValue > 0 
                  ? `${((stats.outstandingCommission / stats.totalCommissionValue) * 100).toFixed(1)}% pending`
                  : '0% pending'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'rgba(156, 39, 176, 0.1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(156, 39, 176, 0.3)',
            borderRadius: 2
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DescriptionIcon sx={{ color: '#9c27b0', mr: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Contract Status
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#9c27b0' }}>
                {stats.liveContracts}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Live • {stats.pendingContracts} Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contract Renewals - Upcoming Expiries */}
      {(() => {
        const today = new Date();
        const threeMonthsFromNow = new Date();
        threeMonthsFromNow.setMonth(today.getMonth() + 3);

        const upcomingRenewals = contracts.filter(contract => {
          if (!contract.contractEndDate) return false;
          const endDate = new Date(contract.contractEndDate);
          return endDate >= today && endDate <= threeMonthsFromNow;
        }).sort((a, b) => {
          const dateA = new Date(a.contractEndDate!).getTime();
          const dateB = new Date(b.contractEndDate!).getTime();
          return dateA - dateB;
        });

        if (upcomingRenewals.length > 0) {
          return (
            <Card sx={{
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              borderRadius: 2,
              mb: 3
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#ff9800' }}>
                  ⚠️ Upcoming Contract Renewals ({upcomingRenewals.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Contracts expiring in the next 3 months
                </Typography>
                <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell>Service Type</TableCell>
                        <TableCell align="right">Contract Value</TableCell>
                        <TableCell align="right">My Commission</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Days Until Expiry</TableCell>
                        <TableCell>Urgency</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {upcomingRenewals.map((contract) => {
                        const contractValue = Number(contract.contractValue) || 0;
                        const commissionRate = contract.subBroker?.commissionRate || 0;
                        const myCommission = contractValue * (commissionRate / 100);
                        const endDate = new Date(contract.contractEndDate!);
                        const daysUntilExpiry = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                        
                        const getUrgencyColor = (days: number) => {
                          if (days <= 30) return 'error';
                          if (days <= 60) return 'warning';
                          return 'info';
                        };

                        const getUrgencyLabel = (days: number) => {
                          if (days <= 30) return 'CRITICAL';
                          if (days <= 60) return 'WARNING';
                          return 'OK';
                        };

                        return (
                          <TableRow key={contract._id} hover>
                            <TableCell>{contract.client?.companyName || 'N/A'}</TableCell>
                            <TableCell>{contract.serviceType}</TableCell>
                            <TableCell align="right">{formatCurrency(contractValue)}</TableCell>
                            <TableCell align="right">{formatCurrency(myCommission)}</TableCell>
                            <TableCell>{endDate.toLocaleDateString('en-GB')}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {daysUntilExpiry} days
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={getUrgencyLabel(daysUntilExpiry)}
                                size="small"
                                color={getUrgencyColor(daysUntilExpiry)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          );
        }
        return null;
      })()}

      {/* Contracts Table */}
      <Card sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(4px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2
      }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            My Contracts
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Service Type</TableCell>
                  <TableCell align="right">Contract Value</TableCell>
                  <TableCell align="right">My Commission Rate</TableCell>
                  <TableCell align="right">My Commission</TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No contracts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  contracts.map((contract) => {
                    const contractValue = Number(contract.contractValue) || 0;
                    const commissionRate = contract.subBroker?.commissionRate || 0;
                    const myCommission = contractValue * (commissionRate / 100);
                    
                    const totalReceived = Number(contract.totalCommissionReceived) || 0;
                    const receivedPercentage = contractValue > 0 ? totalReceived / contractValue : 0;
                    const myReceivedCommission = myCommission * receivedPercentage;
                    const myOutstanding = myCommission - myReceivedCommission;

                    return (
                      <TableRow key={contract._id} hover>
                        <TableCell>{contract.client?.companyName || 'N/A'}</TableCell>
                        <TableCell>{contract.serviceType}</TableCell>
                        <TableCell align="right">{formatCurrency(contractValue)}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${commissionRate}%`}
                            size="small"
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(myCommission)}</TableCell>
                        <TableCell align="right">{formatCurrency(myReceivedCommission)}</TableCell>
                        <TableCell align="right">{formatCurrency(myOutstanding)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={contract.status}
                            size="small"
                            color={getStatusColor(contract.status || '')}
                          />
                        </TableCell>
                        <TableCell>
                          {contract.contractStartDate 
                            ? new Date(contract.contractStartDate).toLocaleDateString('en-GB')
                            : 'N/A'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SubBrokerDashboard;
